import { useState, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { tokens, ToggleGroup } from '@hotjar/hotsauce';
import { LocalStorage } from '@hotjar/insights-common/lib/storage-factory';

import type { Site } from '@hotjar/webapp/domains/common/feature';
import {
  useSliderVisibility,
  DEFAULT_TIMEOUT_MS,
} from '@hotjar/recordings/src/features/RecordingsPage/hooks/useSliderVisibility';
import { EventBoxHolder } from '@hotjar/recordings/src/features/PlayerPage/components/EventBoxHolder';
import type { ActionFilter } from '@hotjar/recordings/src/features/PlayerPage/components/EventBoxHolder/types';
import { ACTION_FILTERS } from '@hotjar/recordings/src/features/PlayerPage/components/EventBoxHolder/constants';
import type { Recording } from '../types';
import type { SessionInfoProps } from '../SessionInfo';
import { SessionInfo } from '../SessionInfo';
import { CloseButton } from './CloseButton';

const StyledToggleContainer = styled.div`
  width: 100%;
  padding: ${tokens.spacer.x1};

  display: flex;
  align-items: center;
  justify-content: space-between;
`;

type TabType = 'info' | 'events';
type HeaderProps = {
  activePanel: TabType;
  onChange: (type: TabType) => void;
};

const Header = ({ onChange, activePanel }: HeaderProps): JSX.Element => {
  const { t } = useTranslation();

  const onKeyDownTab = useCallback((e) => e.stopPropagation(), []);
  const onClickTab = useCallback(
    (e) => {
      onChange(e.target.value as TabType);

      // "Enter" key presses generate a PointerEvent, we need this
      // to prevent the left / right hotkeys from working on mouse clicks
      if (e.nativeEvent.pointerType !== '') {
        (document.activeElement as HTMLElement)?.blur();
      }
    },
    [onChange],
  );

  return (
    <StyledToggleContainer>
      <ToggleGroup
        description="Side panel toggle"
        selectedValue={activePanel}
        onChange={(tabType) => onChange(tabType as TabType)}
      >
        <ToggleGroup.Toggle
          value="info"
          key="info"
          aria-label="Open info panel"
          onClick={onClickTab}
          onKeyDown={onKeyDownTab}
        >
          {t('common:player.side-panel.nav.info')}
        </ToggleGroup.Toggle>
        <ToggleGroup.Toggle
          value="events"
          key="events"
          aria-label="Open events panel"
          onClick={onClickTab}
          onKeyDown={onKeyDownTab}
        >
          {t('common:player.side-panel.nav.actions')}
        </ToggleGroup.Toggle>
      </ToggleGroup>

      <CloseButton />
    </StyledToggleContainer>
  );
};

type Props = {
  recording: Recording;
  showSidePanel: boolean;
  site: Site | null;
};

const SliderContainer = styled.div<{ open: boolean }>`
  width: ${({ open }) => (open ? '300px' : 0)};
  transition: width ease-in-out ${DEFAULT_TIMEOUT_MS}ms;
`;

const Container = styled.div`
  user-select: text;
  width: 300px;
  height: 100%;
  z-index: 10;
  background: ${tokens.color.white};
  border-left: 1px solid ${tokens.color.ui_border};

  display: flex;
  flex-direction: column;
`;

export type SidePanelProps = Omit<SessionInfoProps, 'onActionClick'> & Props;

const SidePanelUnMemo = ({
  showSidePanel,
  recording,
  sidePanelEventMarkers,
  onCreatePollClick,
  showPollUpsell,
  site,
}: SidePanelProps): JSX.Element | null => {
  const [actionFilters, setActionFilters] = useState<ActionFilter[] | undefined>(
    Object.keys(ACTION_FILTERS) as ActionFilter[],
  );
  const [activePanel, setActivePanel] = useState(
    LocalStorage.get('playbackSidePanelTab', 'info') as TabType,
  );

  const onChangeActivePanel = useCallback(
    (tab: TabType) => {
      setActivePanel(tab);
      LocalStorage.set('playbackSidePanelTab', tab);
    },
    [setActivePanel],
  );

  const onActionClick = useCallback(
    (type?: ActionFilter) => {
      const filters = type ? [type] : (Object.keys(ACTION_FILTERS) as ActionFilter[]);

      setActionFilters(filters);
      setActivePanel('events');
    },
    [setActionFilters],
  );

  const { visible, open } = useSliderVisibility(showSidePanel);
  if (!visible) {
    return null;
  }

  return (
    <SliderContainer open={open} className="side-panel">
      <Container>
        <Header onChange={onChangeActivePanel} activePanel={activePanel} />

        {activePanel === 'events' && (
          <EventBoxHolder
            sidePanelEventMarkers={sidePanelEventMarkers}
            actionFilters={actionFilters}
            onActionFilterChange={setActionFilters}
          />
        )}

        {activePanel === 'info' && (
          <SessionInfo
            recording={recording}
            site={site}
            sidePanelEventMarkers={sidePanelEventMarkers}
            showPollUpsell={showPollUpsell}
            onCreatePollClick={onCreatePollClick}
            onActionClick={onActionClick}
          />
        )}
      </Container>
    </SliderContainer>
  );
};

export const SidePanel = memo(SidePanelUnMemo);
